import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Wrench, Droplets, Eye, EyeOff, ArrowLeft, Check, MapPin, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const GIROS = [
  'Taller mecanico general','Taller especializado','Hojalateria y pintura','Electrico automotriz',
  'Car wash','Detallado automotriz','Llantera','Refaccionaria','Otro'
];

const COMO_CONOCIO = [
  'Facebook','Instagram','Google','Recomendacion','WhatsApp','TikTok','Otro'
];

export default function Registro() {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(1);
  const [paises, setPaises] = useState([]);
  const [form, setForm] = useState({
    tipo_negocio: '',
    nombre: '',
    email: '',
    confirmarEmail: '',
    password: '',
    confirmarPassword: '',
    nombre_negocio: '',
    telefono: '',
    pais: 'MX',
    ciudad: '',
    estado_geo: '',
    direccion: '',
    codigo_postal: '',
    giro: '',
    num_empleados: '',
    como_conocio: '',
  });
  const [mostrarPwd, setMostrarPwd] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);

  // Load countries catalog
  useEffect(() => {
    api.get('/paises').then(r => setPaises(r.data)).catch(() => {});
  }, []);

  const paisActual = paises.find(p => p.codigo === form.pais) || {};
  const estadosPais = paisActual.estados || [];

  // Generate browser fingerprint
  const getFingerprint = () => {
    const d = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.hardwareConcurrency || '',
      navigator.platform || '',
    ].join('|');
    let hash = 0;
    for (let i = 0; i < d.length; i++) {
      const c = d.charCodeAt(i);
      hash = ((hash << 5) - hash) + c;
      hash |= 0;
    }
    return Math.abs(hash).toString(36);
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleRegistro = async () => {
    if (!aceptaTerminos) { toast.error('Debes aceptar los terminos y condiciones'); return; }
    if (!form.nombre.trim() || !form.email.trim() || !form.password || !form.nombre_negocio.trim()) {
      toast.error('Todos los campos marcados son obligatorios');
      return;
    }
    if (form.email !== form.confirmarEmail) { toast.error('Los correos electronicos no coinciden'); return; }
    if (form.password.length < 8) { toast.error('La contrasena debe tener al menos 8 caracteres'); return; }
    if (form.password !== form.confirmarPassword) { toast.error('Las contrasenas no coinciden'); return; }
    setCargando(true);
    try {
      const { confirmarEmail, confirmarPassword, ...datos } = form;
      // Concatenate country code with phone number
      const codigoTel = (paisActual.codigo_tel || '+52').replace('+', '');
      const telLimpio = datos.telefono.replace(/\D/g, '');
      datos.telefono = telLimpio.startsWith(codigoTel) ? telLimpio : codigoTel + telLimpio;
      const res = await api.post('/auth/registro-publico', { ...datos, fingerprint: getFingerprint() });
      toast.success('Cuenta creada. Verifica tu correo para continuar');
      navigate(`/verificacion-pendiente?email=${encodeURIComponent(form.email)}&uid=${res.data.user_id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error al crear cuenta';
      toast.error(detail);
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-12" data-testid="registro-page">
      <div className="w-full max-w-lg animate-fadeIn">
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-300 text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </button>
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-sm bg-amber-500/20 flex items-center justify-center">
              <Wrench className="w-6 h-6 text-amber-500" />
            </div>
          </div>
          <h1 className="font-barlow text-2xl font-bold text-slate-50 uppercase">Crear tu cuenta</h1>
          <p className="text-slate-400 text-sm mt-1">7 dias de prueba gratis - sin tarjeta</p>
        </div>

        {/* Step 1: Choose business type */}
        {paso === 1 && (
          <div className="space-y-4">
            <p className="text-center text-slate-300 text-sm font-medium mb-2">Que tipo de negocio tienes?</p>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => { set('tipo_negocio', 'taller'); setPaso(2); }}
                className="group relative bg-slate-900/60 border-2 border-slate-700 hover:border-amber-500 rounded-md p-6 text-center transition-all"
                data-testid="tipo-taller-btn"
              >
                <div className="w-14 h-14 rounded-sm bg-amber-500/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-amber-500/20 transition-colors">
                  <Wrench className="w-7 h-7 text-amber-500" />
                </div>
                <h3 className="font-barlow font-bold text-slate-100 text-lg">Taller</h3>
                <p className="text-xs text-slate-500 mt-1">Mecanico automotriz</p>
              </button>
              <button
                onClick={() => { set('tipo_negocio', 'carwash'); setPaso(2); }}
                className="group relative bg-slate-900/60 border-2 border-slate-700 hover:border-cyan-400 rounded-md p-6 text-center transition-all"
                data-testid="tipo-carwash-btn"
              >
                <div className="w-14 h-14 rounded-sm bg-cyan-500/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-cyan-500/20 transition-colors">
                  <Droplets className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="font-barlow font-bold text-slate-100 text-lg">Car Wash</h3>
                <p className="text-xs text-slate-500 mt-1">Autolavado</p>
              </button>
            </div>
            <div className="text-center pt-4">
              <p className="text-xs text-slate-500">
                Ya tienes cuenta?{' '}
                <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium">Iniciar sesion</Link>
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Business info */}
        {paso === 2 && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <button onClick={() => setPaso(1)} className="text-sm text-slate-500 hover:text-slate-300 flex items-center gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Cambiar tipo
                </button>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${form.tipo_negocio === 'carwash' ? 'bg-cyan-500/10 text-cyan-400' : 'bg-amber-500/10 text-amber-400'}`}>
                  {form.tipo_negocio === 'carwash' ? 'Car Wash' : 'Taller Mecanico'} - Paso 1 de 2
                </span>
              </div>

              <div className="bg-amber-500/5 border border-amber-500/20 rounded-md px-4 py-3">
                <p className="text-xs text-amber-300/90 leading-relaxed">
                  <strong>Importante:</strong> Estos datos apareceran en tus ordenes de servicio, cotizaciones y recibos que entregues a tus clientes. Usa informacion real para proyectar profesionalismo y confianza.
                </p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nombre del negocio *</Label>
                <Input value={form.nombre_negocio} onChange={e => set('nombre_negocio', e.target.value)}
                  placeholder={form.tipo_negocio === 'carwash' ? 'Ej: AquaClean Car Wash' : 'Ej: Taller AutoMax'}
                  className="bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500"
                  data-testid="registro-nombre-negocio" />
              </div>

              {/* Country selector */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1"><Globe className="w-3 h-3" /> Pais *</Label>
                <Select value={form.pais} onValueChange={v => { set('pais', v); set('estado_geo', ''); }}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="registro-pais">
                    <SelectValue placeholder="Seleccionar pais" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {paises.map(p => (
                      <SelectItem key={p.codigo} value={p.codigo} className="text-slate-300">{p.nombre} ({p.codigo})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Tu nombre *</Label>
                  <Input value={form.nombre} onChange={e => set('nombre', e.target.value)}
                    placeholder="Nombre completo"
                    className="bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500"
                    data-testid="registro-nombre" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Telefono * {paisActual.codigo_tel && <span className="text-amber-500">({paisActual.codigo_tel})</span>}</Label>
                  <div className="flex gap-1">
                    <span className="inline-flex items-center px-3 bg-slate-800 border border-slate-700 rounded-sm text-sm text-amber-400 font-mono shrink-0">{paisActual.codigo_tel || '+52'}</span>
                    <Input value={form.telefono} onChange={e => set('telefono', e.target.value.replace(/\D/g, ''))}
                      placeholder="10 digitos"
                      maxLength={10}
                      className="bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500 font-mono"
                      data-testid="registro-telefono" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Ciudad *</Label>
                  <Input value={form.ciudad} onChange={e => set('ciudad', e.target.value)}
                    placeholder="Ej: Monterrey"
                    className="bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500"
                    data-testid="registro-ciudad" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Estado / Provincia *</Label>
                  <Select value={form.estado_geo} onValueChange={v => set('estado_geo', v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="registro-estado-geo">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                      {estadosPais.map(e => (
                        <SelectItem key={e} value={e} className="text-slate-300">{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Direccion</Label>
                  <Input value={form.direccion} onChange={e => set('direccion', e.target.value)}
                    placeholder="Calle, numero, colonia"
                    className="bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500"
                    data-testid="registro-direccion" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">C.P.</Label>
                  <Input value={form.codigo_postal} onChange={e => set('codigo_postal', e.target.value)}
                    placeholder="00000"
                    className="bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500"
                    data-testid="registro-cp" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Giro del negocio</Label>
                  <Select value={form.giro} onValueChange={v => set('giro', v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="registro-giro">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {GIROS.map(g => (
                        <SelectItem key={g} value={g} className="text-slate-300">{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Num. empleados</Label>
                  <Select value={form.num_empleados} onValueChange={v => set('num_empleados', v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="registro-num-empleados">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="1-2" className="text-slate-300">1-2</SelectItem>
                      <SelectItem value="3-5" className="text-slate-300">3-5</SelectItem>
                      <SelectItem value="6-10" className="text-slate-300">6-10</SelectItem>
                      <SelectItem value="11-20" className="text-slate-300">11-20</SelectItem>
                      <SelectItem value="20+" className="text-slate-300">20+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Como conociste Talleres PRO?</Label>
                <Select value={form.como_conocio} onValueChange={v => set('como_conocio', v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="registro-como-conocio">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {COMO_CONOCIO.map(c => (
                      <SelectItem key={c} value={c} className="text-slate-300">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={() => {
                if (!form.nombre_negocio.trim()) { toast.error('El nombre del negocio es obligatorio'); return; }
                if (!form.nombre.trim()) { toast.error('Tu nombre es obligatorio'); return; }
                if (!form.telefono.trim()) { toast.error('El telefono es obligatorio'); return; }
                if (!form.ciudad.trim()) { toast.error('La ciudad es obligatoria'); return; }
                if (!form.estado_geo) { toast.error('El estado es obligatorio'); return; }
                setPaso(3);
              }}
                className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm"
                data-testid="registro-siguiente-btn">
                Siguiente
              </Button>

              <p className="text-center text-xs text-slate-500">
                Ya tienes cuenta?{' '}
                <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium">Iniciar sesion</Link>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Account credentials */}
        {paso === 3 && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <button onClick={() => setPaso(2)} className="text-sm text-slate-500 hover:text-slate-300 flex items-center gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Datos del negocio
                </button>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${form.tipo_negocio === 'carwash' ? 'bg-cyan-500/10 text-cyan-400' : 'bg-amber-500/10 text-amber-400'}`}>
                  Paso 2 de 2
                </span>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Correo electronico *</Label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  placeholder="tu@email.com"
                  className="bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500"
                  data-testid="registro-email" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Confirmar correo electronico *</Label>
                <Input type="email" value={form.confirmarEmail} onChange={e => set('confirmarEmail', e.target.value)}
                  placeholder="Repite tu correo"
                  className={`bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500 ${form.confirmarEmail && form.email !== form.confirmarEmail ? 'border-red-500 focus:border-red-500' : ''}`}
                  data-testid="registro-confirmar-email" />
                {form.confirmarEmail && form.email !== form.confirmarEmail && (
                  <p className="text-xs text-red-400 mt-1">Los correos no coinciden</p>
                )}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Contrasena *</Label>
                <div className="relative">
                  <Input type={mostrarPwd ? 'text' : 'password'} value={form.password}
                    onChange={e => set('password', e.target.value)}
                    placeholder="Minimo 8 caracteres"
                    className="bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500 pr-10"
                    data-testid="registro-password" />
                  <button type="button" onClick={() => setMostrarPwd(!mostrarPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {mostrarPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Confirmar contrasena *</Label>
                <Input type={mostrarPwd ? 'text' : 'password'} value={form.confirmarPassword}
                  onChange={e => set('confirmarPassword', e.target.value)}
                  placeholder="Repite tu contrasena"
                  className={`bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500 ${form.confirmarPassword && form.password !== form.confirmarPassword ? 'border-red-500 focus:border-red-500' : ''}`}
                  data-testid="registro-confirmar-password" />
                {form.confirmarPassword && form.password !== form.confirmarPassword && (
                  <p className="text-xs text-red-400 mt-1">Las contrasenas no coinciden</p>
                )}
              </div>

              <label className="flex items-start gap-2 cursor-pointer pt-1" data-testid="registro-terminos-check">
                <input type="checkbox" checked={aceptaTerminos} onChange={e => setAceptaTerminos(e.target.checked)}
                  className="mt-1 rounded border-slate-600 bg-slate-950 text-amber-500 focus:ring-amber-500" />
                <span className="text-xs text-slate-400 leading-relaxed">
                  Acepto los{' '}
                  <Link to="/terminos" target="_blank" className="text-amber-500 hover:text-amber-400 underline">Terminos y Condiciones</Link>
                  {' '}y el{' '}
                  <Link to="/privacidad" target="_blank" className="text-amber-500 hover:text-amber-400 underline">Aviso de Privacidad</Link>
                </span>
              </label>

              <Button onClick={handleRegistro} disabled={cargando}
                className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)] transition-all"
                data-testid="registro-submit-btn">
                {cargando ? 'Creando cuenta...' : 'Crear Cuenta y Empezar Prueba Gratis'}
              </Button>

              <p className="text-center text-xs text-slate-500">
                Ya tienes cuenta?{' '}
                <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium">Iniciar sesion</Link>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
